import { useMergeRefs } from "@floating-ui/react"
import type {
  ComponentPropsWithoutRef,
  Ref,
} from "react"
import {
  forwardRef,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react"
import { FormContext } from "./FormContext"

export type Props = ComponentPropsWithoutRef<"form">

/*
 * this form element maintains a context that tracks
 * if the form is currently valid or not
 */
export const Form = forwardRef<HTMLFormElement, Props>(
  function Form(props: Props, propRef: Ref<HTMLFormElement>) {
    const [
      isValid,
      setIsValid,
    ] = useState<boolean>(false)

    const formRef = useRef<HTMLFormElement>(null)

    const recheckValidity = useCallback(
      () => {
        if (formRef.current) {
          setIsValid(formRef.current.checkValidity())
        }
      },
      [
        formRef,
        setIsValid,
      ],
    )

    const formRefCallback = useCallback(
      (form: HTMLFormElement | null) => {
        if (form) {
          setIsValid(form.checkValidity())
        }
      },
      [setIsValid],
    )

    const contextValue = useMemo(
      () => ({
        formRef,
        isValid,
        recheckValidity,
      }),
      [
        formRef,
        isValid,
        recheckValidity,
      ],
    )

    const ref = useMergeRefs([
      formRef,
      formRefCallback,
      propRef,
    ])

    return (
      <FormContext.Provider value={contextValue}>
        <form
          {...props}
          ref={ref}
        />
      </FormContext.Provider>
    )
  },
)
